import { BACKEND_URL } from "../config";
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const OnboardingDashboard = () => {
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [mlPerformance, setMlPerformance] = useState(null);
  // 🔒 PRODUCTION ONLY - No localhost fallback
  const backendUrl = BACKEND_URL;

  useEffect(() => {
    fetchSubscriptionInfo();
    fetchUserStats();
    fetchMLPerformance();
    
    // Refresh ML performance every 60 seconds
    const interval = setInterval(fetchMLPerformance, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/business/subscription/info`);
      const data = await response.json();
      setSubscriptionInfo(data);
    } catch (error) {
      console.error('Error fetching subscription info:', error);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/business/stats`);
      const data = await response.json();
      setUserStats(data);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  const fetchMLPerformance = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/public/ml-pair-performance`);
      const data = await response.json();
      if (data.success) {
        setMlPerformance(data);
      }
    } catch (error) {
      console.error('Error fetching ML performance:', error);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
      padding: '2rem'
    }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }}>
          <h1 style={{ fontSize: '2.5rem', color: '#0891b2', marginBottom: '1rem' }}>
            Welcome to Trade Blindfolded! 🎉
          </h1>
          <p style={{ fontSize: '1.125rem', color: '#334155', marginBottom: '1.5rem' }}>
            You're on your 7-day free trial. Here's how to get started with professional Forex trading:
          </p>

          {/* Trial Status */}
          {subscriptionInfo && (
            <div style={{
              padding: '1rem',
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)',
              border: '2px solid #fbbf24',
              borderRadius: '10px',
              marginBottom: '1rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong style={{ color: '#f59e0b' }}>Trial Status:</strong> {subscriptionInfo.days_remaining} days remaining
                </div>
                {userStats && (
                  <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                    Calculations: {userStats.calculations_this_hour}/{userStats.calculations_limit} this hour
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{
            padding: '1rem',
            background: '#e0f2fe',
            borderRadius: '8px'
          }}>
            <strong style={{ color: '#0891b2' }}>What Happens Next:</strong>
            <ul style={{ marginTop: '0.5rem', marginLeft: '1rem', color: '#334155' }}>
              <li>After 7 days: Get 60 days of demo pricing ($15-$40/month)</li>
              <li>Practice with prop firm demo accounts</li>
              <li>After 60 days: Upgrade to live tier when earning money</li>
            </ul>
          </div>
        </div>

        {/* Getting Started Guide */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }}>
          <h2 style={{ fontSize: '2rem', color: '#0f172a', marginBottom: '1.5rem' }}>
            🚀 Getting Started Guide
          </h2>

          {/* Step 1: FTMO */}
          <div style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            border: '2px solid #fbbf24'
          }}>
            <h3 style={{ fontSize: '1.5rem', color: '#f59e0b', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '2rem' }}>1️⃣</span> Create FTMO Demo Account
            </h3>
            <p style={{ color: '#78350f', marginBottom: '1rem' }}>
              FTMO is the world's leading prop firm. Start with a free demo account to practice risk-free:
            </p>
            <ul style={{ marginLeft: '1.5rem', color: '#78350f', marginBottom: '1rem' }}>
              <li>Practice trading with virtual money</li>
              <li>Learn risk management strategies</li>
              <li>Test your strategies before trading real capital</li>
            </ul>
            <a
              href="https://www.ftmo.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                background: '#f59e0b',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600'
              }}
            >
              Open FTMO Account →
            </a>
          </div>

          {/* Step 2: MT5 */}
          <div style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            border: '2px solid #3b82f6'
          }}>
            <h3 style={{ fontSize: '1.5rem', color: '#3b82f6', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '2rem' }}>2️⃣</span> Download MetaTrader 5
            </h3>
            <p style={{ color: '#1e3a8a', marginBottom: '1rem' }}>
              MT5 is the professional trading platform used by millions worldwide:
            </p>
            <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '1rem' }}>
              <a
                href="https://download.mql5.com/cdn/web/metaquotes.software.corp/mt5/mt5setup.exe"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '0.75rem',
                  background: '#3b82f6',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  textAlign: 'center',
                  fontWeight: '600'
                }}
              >
                🪟 Windows
              </a>
              <a
                href="https://download.mql5.com/cdn/web/metaquotes.software.corp/mt5/MetaTrader5.dmg"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '0.75rem',
                  background: '#3b82f6',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  textAlign: 'center',
                  fontWeight: '600'
                }}
              >
                🍎 Mac
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=net.metaquotes.metatrader5"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '0.75rem',
                  background: '#3b82f6',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  textAlign: 'center',
                  fontWeight: '600'
                }}
              >
                📱 Android
              </a>
              <a
                href="https://apps.apple.com/us/app/metatrader-5/id413251709"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '0.75rem',
                  background: '#3b82f6',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  textAlign: 'center',
                  fontWeight: '600'
                }}
              >
                📱 iOS
              </a>
            </div>
          </div>

          {/* Step 3: The5ers */}
          <div style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            border: '2px solid #22c55e'
          }}>
            <h3 style={{ fontSize: '1.5rem', color: '#16a34a', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '2rem' }}>3️⃣</span> Join The5ers Bootcamp
            </h3>
            <p style={{ color: '#14532d', marginBottom: '1rem' }}>
              Learn professional trading strategies from expert traders:
            </p>
            <ul style={{ marginLeft: '1.5rem', color: '#14532d', marginBottom: '1rem' }}>
              <li>Comprehensive trading education</li>
              <li>Live trading sessions with pros</li>
              <li>Get funded by The5ers after passing evaluation</li>
            </ul>
            <a
              href="https://the5ers.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                background: '#16a34a',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600'
              }}
            >
              Explore The5ers →
            </a>
          </div>
        </div>

        {/* ML Performance Section */}
        {mlPerformance && mlPerformance.pairs && mlPerformance.pairs.length > 0 && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            marginBottom: '2rem',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1.5rem',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <div>
                <h2 style={{ 
                  fontSize: '1.75rem', 
                  color: '#0891b2', 
                  marginBottom: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  🧠 ML Performance - Live Learning
                </h2>
                <p style={{ color: '#64748b', fontSize: '0.95rem' }}>
                  {mlPerformance.message}
                </p>
              </div>
              
              {/* System Stats Badge */}
              <div style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                padding: '1rem 1.5rem',
                borderRadius: '12px',
                textAlign: 'center',
                minWidth: '150px'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                  {mlPerformance.system_stats.overall_win_rate}%
                </div>
                <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                  System Win Rate
                </div>
                <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>
                  {mlPerformance.system_stats.total_trades} trades analyzed
                </div>
              </div>
            </div>

            {/* Pair Performance List */}
            <div style={{
              background: '#f8fafc',
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <h3 style={{
                fontSize: '1.1rem',
                color: '#1e293b',
                marginBottom: '1rem',
                fontWeight: '600'
              }}>
                📈 Top Performing Pairs (All Close Together!)
              </h3>
              
              <div style={{
                display: 'grid',
                gap: '0.75rem'
              }}>
                {mlPerformance.pairs.slice(0, 7).map((pair, index) => {
                  // Determine colors
                  const winRateColor = pair.win_rate >= 60 ? '#10b981' : pair.win_rate >= 50 ? '#f59e0b' : '#ef4444';
                  const trendIcon = pair.trend === 'up' ? '📈' : pair.trend === 'down' ? '📉' : '➡️';
                  const trendColor = pair.trend === 'up' ? '#10b981' : pair.trend === 'down' ? '#ef4444' : '#64748b';
                  
                  return (
                    <div
                      key={pair.pair}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '1rem',
                        background: 'white',
                        borderRadius: '8px',
                        borderLeft: `4px solid ${winRateColor}`,
                        transition: 'transform 0.2s'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                        {/* Rank */}
                        <div style={{ 
                          fontSize: '1.2rem',
                          fontWeight: 'bold',
                          color: '#64748b',
                          minWidth: '35px'
                        }}>
                          #{index + 1}
                        </div>
                        
                        {/* Pair */}
                        <div style={{ 
                          fontSize: '1.1rem',
                          fontWeight: '600',
                          color: '#1e293b',
                          minWidth: '90px'
                        }}>
                          {pair.pair}
                        </div>
                        
                        {/* Record */}
                        <div style={{
                          fontSize: '0.9rem',
                          color: '#64748b',
                          minWidth: '70px'
                        }}>
                          {pair.wins}W/{pair.losses}L
                        </div>
                        
                        {/* Win Rate */}
                        <div style={{
                          fontSize: '1.25rem',
                          fontWeight: 'bold',
                          color: winRateColor,
                          minWidth: '70px'
                        }}>
                          {pair.win_rate}%
                        </div>
                      </div>
                      
                      {/* Trend */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.85rem',
                        color: trendColor,
                        fontWeight: '600'
                      }}>
                        {trendIcon} {pair.progression_display}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Insight Message */}
              <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderRadius: '8px',
                border: '2px solid #fbbf24'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  color: '#92400e'
                }}>
                  <span style={{ fontSize: '1.5rem' }}>💡</span>
                  <div>
                    <strong>Notice the tight clustering?</strong> Unlike traditional systems where some pairs dominate, 
                    our ML learns each pair's optimal scenarios - bringing ALL pairs to high performance levels. 
                    This is continuous learning in action!
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>Ready to Trade?</h2>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link
              to="/"
              style={{
                padding: '1rem 2rem',
                background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '1.125rem'
              }}
            >
              📊 Start Calculating Trades
            </Link>
            <Link
              to="/business"
              style={{
                padding: '1rem 2rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '1.125rem'
              }}
            >
              💼 Invite Friends & Earn
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingDashboard;
